import React, { useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Bot, Sparkles, Copy, Check, Send, History, Cog } from 'lucide-react';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Textarea } from '../components/Textarea';
import { Avatar, AvatarFallback } from '../components/Avatar';
import { toast } from 'sonner';
const toneProfile = [{
  name: 'Formal',
  value: 85,
  color: 'bg-primary'
}, {
  name: 'Empatik',
  value: 72,
  color: 'bg-emerald-500'
}, {
  name: 'Tegas',
  value: 68,
  color: 'bg-amber-500'
}, {
  name: 'Reflektif',
  value: 54,
  color: 'bg-purple-500'
}, {
  name: 'Inspiratif',
  value: 78,
  color: 'bg-blue-500'
}];
const vocabulary = ['Gotong Royong', 'Sila Pancasila', 'Indonesia Maju', 'Hilirisasi', 'Reformasi Birokrasi', 'Anak Muda', 'Bangsa', 'Saudara-saudara', 'NKRI', 'Bonus Demografi'];
const preferences = ['Mulai dengan salam pembuka', 'Ucapkan terima kasih atas perhatian publik', 'Sebut tantangan terlebih dahulu sebelum solusi', 'Akhiri dengan ajakan persatuan', 'Gunakan kalimat pendek dan langsung'];
const generatedResponses = [{
  id: 'A',
  title: 'Opsi A: Empatik & Reflektif',
  tone: 'Empatik',
  text: 'Saudara-saudara sebangsa dan setanah air,\n\nSaya memahami kekhawatiran yang muncul belakangan ini terkait distribusi bansos pangan. Pemerintah hadir bukan hanya pada momen baik, tetapi terutama saat rakyat membutuhkan kepastian.\n\nSaya telah menginstruksikan Menteri Sosial dan Bulog untuk memastikan tidak ada satupun keluarga prasejahtera yang tertinggal. Mari kita lawan hoaks bersama dengan kekuatan informasi yang benar.\n\nKita Indonesia. Kita gotong royong. Terima kasih.',
  reception: 78,
  sentimentUplift: 12,
  risk: null
}, {
  id: 'B',
  title: 'Opsi B: Formal & Otoritatif',
  tone: 'Formal',
  text: 'Sebagai Kepala Negara, saya menegaskan bahwa Pemerintah Republik Indonesia berkomitmen penuh terhadap kelancaran penyaluran bansos pangan kepada seluruh keluarga penerima manfaat.\n\nKementerian Sosial telah melakukan verifikasi dan distribusi sesuai data terpadu. Setiap informasi yang bertentangan dengan fakta resmi akan ditindak sesuai ketentuan hukum.\n\nMasyarakat dihimbau untuk mengakses informasi hanya dari kanal resmi pemerintah.',
  reception: 65,
  sentimentUplift: 6,
  risk: null
}, {
  id: 'C',
  title: 'Opsi C: Tegas & Direct',
  tone: 'Tegas',
  text: 'Bansos pangan jalan terus. Tidak ada penghentian. Hoaks yang beredar adalah kebohongan.\n\nSaya perintahkan Polri menindak tegas penyebar disinformasi. Negara tidak akan tinggal diam terhadap aktor yang mempermainkan kebutuhan dasar rakyat.\n\nRakyat tenang. Pemerintah bekerja.',
  reception: 54,
  sentimentUplift: 3,
  risk: 'Risiko polarisasi narasi anti-kritik'
}];
const historicalPatterns = [{
  date: '12 Mar 2026',
  issue: 'Hoaks kelangkaan beras',
  tone: 'Empatik',
  result: '+18%',
  rating: 92
}, {
  date: '04 Feb 2026',
  issue: 'Demo BBM nasional',
  tone: 'Empatik',
  result: '+9%',
  rating: 81
}, {
  date: '21 Jan 2026',
  issue: 'Penolakan UU Cipta Kerja',
  tone: 'Formal',
  result: '+4%',
  rating: 67
}, {
  date: '08 Des 2025',
  issue: 'Deepfake video Presiden',
  tone: 'Tegas',
  result: '−2%',
  rating: 58
}, {
  date: '15 Nov 2025',
  issue: 'Banjir Jakarta',
  tone: 'Empatik',
  result: '+24%',
  rating: 95
}];
export function PresidentialDigitalTwin() {
  const [issue, setIssue] = useState('Hoaks distribusi bansos pangan yang menyebar di WhatsApp dan TikTok dalam 6 jam terakhir, sudah menjangkau 850K interaksi negatif.');
  const [showResponses, setShowResponses] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const handleGenerate = () => {
    if (!issue.trim()) {
      toast.error('Isi konteks isu terlebih dahulu');
      return;
    }
    setShowResponses(true);
    toast.success('Respons Presidensial dihasilkan');
  };
  const handleCopy = (id: string, text: string) => {
    navigator.clipboard?.writeText(text);
    setCopiedId(id);
    toast.success('Teks disalin ke clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6 pb-10" data-id="element-2761">
      <PageHeader group="Intelijen Presiden" title="Presidential Digital Twin" description="Model AI gaya komunikasi Presiden — simulasikan respons strategis sesuai pola komunikasi otentik kepala negara." icon={Bot} data-id="element-2762" />

      {/* ROW 1: Twin Profile */}
      <Card className="bg-card border-border/50" data-id="element-2763">
        <CardContent className="p-5" data-id="element-2764">
          <div className="grid grid-cols-12 gap-6" data-id="element-2765">
            <div className="col-span-12 md:col-span-3 flex flex-col items-center text-center md:border-r md:border-border/50 md:pr-6" data-id="element-2766">
              <Avatar className="size-24 border-2 border-primary/30 mb-3" data-id="element-2767">
                <AvatarFallback className="bg-primary/10 text-primary text-3xl font-bold" data-id="element-2768">
                  PDT
                </AvatarFallback>
              </Avatar>
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono text-[10px] mb-2" data-id="element-2769">
                <span className="size-1.5 rounded-full bg-emerald-500 mr-1.5 inline-block" data-id="element-2770" />
                TRAINED · 96.4%
              </Badge>
              <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-2771">
                Model: PDT v3.2
              </p>
              <p className="font-mono text-[9px] text-muted-foreground mt-1" data-id="element-2772">
                Update: 12 jam lalu
              </p>
            </div>

            <div className="col-span-12 md:col-span-9 grid grid-cols-1 lg:grid-cols-3 gap-5" data-id="element-2773">
              {/* Tone Profile */}
              <div data-id="element-2774">
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-3" data-id="element-2775">
                  Tone Profile
                </p>
                <div className="space-y-2" data-id="element-2776">
                  {toneProfile.map(t => <div key={t.name} data-id="element-2777">
                      <div className="flex justify-between text-xs mb-1" data-id="element-2778">
                        <span className="font-medium" data-id="element-2779">{t.name}</span>
                        <span className="font-mono text-muted-foreground" data-id="element-2780">
                          {t.value}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden" data-id="element-2781">
                        <div className={`h-full ${t.color}`} style={{
                      width: `${t.value}%`
                    }} data-id="element-2782" />
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Vocabulary */}
              <div data-id="element-2783">
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-3" data-id="element-2784">
                  Vocabulary Cluster
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-2785">
                  {vocabulary.map(v => <span key={v} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-primary/5 border border-primary/20 text-primary" data-id="element-2786">
                      {v}
                    </span>)}
                </div>
              </div>

              {/* Preferences */}
              <div data-id="element-2787">
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-3" data-id="element-2788">
                  Preferensi Komunikasi
                </p>
                <ul className="space-y-1.5" data-id="element-2789">
                  {preferences.map((p, i) => <li key={i} className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-2790">
                      <span className="mt-1 size-1 rounded-full bg-primary shrink-0" data-id="element-2791" />
                      {p}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ROW 2: Response Simulator */}
      <Card className="bg-card border-primary/30" data-id="element-2792">
        <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50 bg-primary/5" data-id="element-2793">
          <CardTitle className="font-mono text-sm uppercase tracking-wider text-primary flex items-center gap-2" data-id="element-2794">
            <Sparkles className="size-4" data-id="element-2795" />
            Simulasi Respons Presidensial
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5" data-id="element-2796">
          <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2" data-id="element-2797">
            Konteks Isu
          </p>
          <Textarea value={issue} onChange={e => setIssue(e.target.value)} placeholder="Masukkan isu yang perlu direspons Presiden..." className="min-h-[100px] text-sm" data-id="element-2798" />
          <div className="flex justify-end mt-3" data-id="element-2799">
            <Button onClick={handleGenerate} data-id="element-2800">
              <Sparkles className="size-3.5 mr-2" data-id="element-2801" /> Generate Respons
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ROW 3: 3 Generated Responses */}
      {showResponses && <div className="grid grid-cols-12 gap-4" data-id="element-2802">
          {generatedResponses.map(r => <Card key={r.id} className={`col-span-12 lg:col-span-4 bg-card border-border/50 flex flex-col ${r.id === 'A' ? 'ring-2 ring-emerald-500/30' : ''}`} data-id="element-2803">
              <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50" data-id="element-2804">
                <div className="flex items-center justify-between mb-1" data-id="element-2805">
                  <Badge variant="outline" className="font-mono text-[9px]" data-id="element-2806">
                    {r.tone}
                  </Badge>
                  {r.id === 'A' && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono text-[9px]" data-id="element-2807">
                      AI: REKOMENDASI
                    </Badge>}
                </div>
                <CardTitle className="text-sm font-bold text-foreground" data-id="element-2808">
                  {r.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5 flex-1 flex flex-col" data-id="element-2809">
                <div className="bg-muted/20 border border-border/40 rounded-md p-3 text-xs leading-relaxed text-foreground whitespace-pre-line flex-1 mb-4 italic" data-id="element-2810">
                  {r.text}
                </div>

                <div className="space-y-2 mb-4" data-id="element-2811">
                  <div data-id="element-2812">
                    <div className="flex justify-between text-[10px] mb-1" data-id="element-2813">
                      <span className="font-mono uppercase text-muted-foreground" data-id="element-2814">
                        Public Reception
                      </span>
                      <span className="font-mono font-bold text-foreground" data-id="element-2815">
                        {r.reception}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden" data-id="element-2816">
                      <div className={`h-full ${r.reception >= 70 ? 'bg-emerald-500' : r.reception >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                  width: `${r.reception}%`
                }} data-id="element-2817" />
                    </div>
                  </div>
                  <div className="flex justify-between text-[10px] font-mono" data-id="element-2818">
                    <span className="text-muted-foreground uppercase" data-id="element-2819">
                      Sentiment Uplift
                    </span>
                    <span className={r.sentimentUplift >= 10 ? 'text-emerald-500 font-bold' : r.sentimentUplift >= 5 ? 'text-amber-500 font-bold' : 'text-red-500 font-bold'} data-id="element-2820">
                      +{r.sentimentUplift}%
                    </span>
                  </div>
                </div>

                {r.risk && <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-[10px] text-amber-800" data-id="element-2821">
                    <strong data-id="element-2822">⚠ Perhatian:</strong> {r.risk}
                  </div>}

                <div className="flex gap-2" data-id="element-2823">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => handleCopy(r.id, r.text)} data-id="element-2824">
                    {copiedId === r.id ? <Check className="size-3.5 mr-1.5" data-id="element-2825" /> : <Copy className="size-3.5 mr-1.5" data-id="element-2826" />}
                    Salin
                  </Button>
                  <Button size="sm" className="flex-1" onClick={() => toast.success(`Respons ${r.id} dipilih`, {
              description: 'Disiapkan untuk eksekusi.'
            })} data-id="element-2827">
                    <Send className="size-3.5 mr-1.5" data-id="element-2828" /> Pilih
                  </Button>
                </div>
              </CardContent>
            </Card>)}
        </div>}

      {/* ROW 4: Historical Pattern */}
      <Card className="bg-card border-border/50" data-id="element-2829">
        <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50" data-id="element-2830">
          <CardTitle className="font-mono text-sm uppercase tracking-wider flex items-center gap-2" data-id="element-2831">
            <History className="size-4 text-primary" data-id="element-2832" />
            Pola Historis yang Mirip
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0" data-id="element-2833">
          <table className="w-full text-left" data-id="element-2834">
            <thead className="bg-muted/20 border-b border-border/50" data-id="element-2835">
              <tr data-id="element-2836">
                <th className="font-mono text-[10px] uppercase text-muted-foreground p-3" data-id="element-2837">
                  Tanggal
                </th>
                <th className="font-mono text-[10px] uppercase text-muted-foreground p-3" data-id="element-2838">
                  Isu
                </th>
                <th className="font-mono text-[10px] uppercase text-muted-foreground p-3" data-id="element-2839">
                  Tone
                </th>
                <th className="font-mono text-[10px] uppercase text-muted-foreground p-3" data-id="element-2840">
                  Δ Sentimen
                </th>
                <th className="font-mono text-[10px] uppercase text-muted-foreground p-3 text-right" data-id="element-2841">
                  Efektivitas
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50" data-id="element-2842">
              {historicalPatterns.map((p, i) => <tr key={i} className="hover:bg-muted/20" data-id="element-2843">
                  <td className="p-3 font-mono text-[10px] text-muted-foreground" data-id="element-2844">
                    {p.date}
                  </td>
                  <td className="p-3 text-xs font-medium text-foreground" data-id="element-2845">
                    {p.issue}
                  </td>
                  <td className="p-3" data-id="element-2846">
                    <Badge variant="outline" className="font-mono text-[9px] h-4" data-id="element-2847">
                      {p.tone}
                    </Badge>
                  </td>
                  <td className={`p-3 font-mono text-xs font-bold ${p.result.startsWith('+') ? 'text-emerald-500' : 'text-red-500'}`} data-id="element-2848">
                    {p.result}
                  </td>
                  <td className="p-3 text-right" data-id="element-2849">
                    <span className={`font-mono text-xs font-bold ${p.rating >= 80 ? 'text-emerald-500' : p.rating >= 60 ? 'text-amber-500' : 'text-red-500'}`} data-id="element-2850">
                      {p.rating}/100
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
          <p className="px-5 py-3 font-mono text-[10px] text-muted-foreground border-t border-border/50" data-id="element-2851">
            AI menemukan pola: Presiden cenderung memilih tone{' '}
            <strong data-id="element-2852">Empatik</strong> untuk isu sosial (<strong data-id="element-2853">87%</strong>{' '}
            kasus).
          </p>
        </CardContent>
      </Card>

      {/* ROW 5: Calibration */}
      <Card className="bg-card border-border/50" data-id="element-2854">
        <CardContent className="p-5" data-id="element-2855">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-2856">
            <div className="flex items-center gap-2" data-id="element-2857">
              <Cog className="size-4 text-muted-foreground" data-id="element-2858" />
              <h4 className="font-mono text-sm uppercase tracking-wider" data-id="element-2859">
                Kalibrasi Digital Twin
              </h4>
            </div>
            <div className="flex flex-wrap gap-6 text-xs" data-id="element-2860">
              <div data-id="element-2861">
                <p className="font-mono text-[9px] uppercase text-muted-foreground" data-id="element-2862">
                  Dataset Training
                </p>
                <p className="font-bold text-foreground" data-id="element-2863">
                  2.847 pidato + 14.200 pernyataan publik
                </p>
              </div>
              <div data-id="element-2864">
                <p className="font-mono text-[9px] uppercase text-muted-foreground" data-id="element-2865">
                  Kalibrasi Terakhir
                </p>
                <p className="font-bold text-foreground" data-id="element-2866">12 jam lalu</p>
              </div>
              <div data-id="element-2867">
                <p className="font-mono text-[9px] uppercase text-muted-foreground" data-id="element-2868">
                  Akurasi Prediksi
                </p>
                <p className="font-bold text-emerald-500" data-id="element-2869">96.4%</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => toast.success('Recalibration scheduled', {
            description: 'Model akan dilatih ulang dalam 24 jam.'
          })} data-id="element-2870">
              <Cog className="size-3.5 mr-1.5" data-id="element-2871" /> Recalibrate
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>;
}